import { FC, Reducer, useReducer } from "react";

import * as Calculator from "./Calculator.styled";
import Slider from "./slider/Slider";
import theme from "./theme";
import { translate } from "./translations";
import { round } from "./utils";
import NumberInput from "./components/NumberInput";

interface CalculateFirstYearInterestArgs {
  loanAmount: number;
  effectiveInterestRatePerPeriod: number;
  monthlyRate: number;
}

const calculateFirstYearInterest = ({
  loanAmount,
  effectiveInterestRatePerPeriod,
  monthlyRate,
}: CalculateFirstYearInterestArgs) => {
  let outstandingDebt = loanAmount;
  let accruedInterests = 0;
  for (let i = 0; i < 12; i++) {
    accruedInterests += outstandingDebt * effectiveInterestRatePerPeriod;
    outstandingDebt =
      outstandingDebt -
      (monthlyRate - outstandingDebt * effectiveInterestRatePerPeriod);
  }

  return accruedInterests;
};

interface AnnuityCalculatorState {
  loanAmount: number;
  durationInMonths: number;
  interestPercentage: number;
  inflationPercentage: number;
  accrualsPerYear: number;
  paymentsPerYear: number;
}

type AnnuityCalculatorAction =
  | {
      type: "setAmount";
      amount: number;
    }
  | {
      type: "setDuration";
      duration: number;
    }
  | {
      type: "setInterest";
      interest: number;
    }
  | {
      type: "setInflation";
      inflation: number;
    }
  | {
      type: "setAccruals";
      accruals: number;
    }
  | {
      type: "setPayments";
      payments: number;
    };

const reducer: Reducer<AnnuityCalculatorState, AnnuityCalculatorAction> = (
  state,
  action
) => {
  switch (action.type) {
    case "setAmount":
      return {
        ...state,
        loanAmount: action.amount,
      };
    case "setDuration":
      return {
        ...state,
        durationInMonths: action.duration,
      };
    case "setInterest":
      return {
        ...state,
        interestPercentage: action.interest,
      };
    case "setInflation":
      return {
        ...state,
        inflationPercentage: action.inflation,
      };
    case "setAccruals":
      return {
        ...state,
        accrualsPerYear: action.accruals,
      };
    case "setPayments":
      return {
        ...state,
        paymentsPerYear: action.payments,
      };
    default:
      throw new Error();
  }
};

interface AnnuityCalculatorProps {
  showAccruals?: boolean;
  showPayments?: boolean;
  showInflation?: boolean;
  showSubsidyAmount?: boolean;
  showPaymentAfterSubsidy?: boolean;
  options: {
    amountMin: number;
    amountMax: number;
    amountIncrement: number;
    amountDefault: number;
    interestMin: number;
    interestMax: number;
    interestIncrement: number;
    interestDefault: number;
    durationMin: number;
    durationMax: number;
    durationIncrement: number;
    durationDefault: number;
    accrualsMin: number;
    accrualsMax: number;
    accrualsIncrement: number;
    accrualsDefault: number;
    paymentsMin: number;
    paymentsMax: number;
    paymentsIncrement: number;
    paymentsDefault: number;
    inflationMin: number;
    inflationMax: number;
    inflationIncrement: number;
    inflationDefault: number;
    interestSubsidy: number;
    interestSubsidyLimit: number;
  };
}

const AnnuityCalculator: FC<AnnuityCalculatorProps> = ({
  showAccruals,
  showPayments,
  showInflation,
  showSubsidyAmount,
  showPaymentAfterSubsidy,
  options,
}) => {
  const initialState: AnnuityCalculatorState = {
    loanAmount: options.amountDefault,
    durationInMonths: options.durationDefault,
    interestPercentage: options.interestDefault,
    inflationPercentage: options.inflationDefault,
    accrualsPerYear: options.accrualsDefault,
    paymentsPerYear: options.paymentsDefault,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const G = state.loanAmount;
  const r = state.interestPercentage / 100;
  const k = state.accrualsPerYear;
  const p = state.paymentsPerYear;
  const d = state.durationInMonths;
  const i = state.inflationPercentage / 100;
  const m = Math.pow(Math.pow(1 + r / k, k), 1 / p);
  const monthlyRate = m === 1 ? G / d : G * ((m - 1) / (1 - Math.pow(m, -d)));
  const interestAfterInflation = (r + 1) / (i + 1) - 1;
  const effectiveInterestRatePerPeriod =
    Math.pow(Math.pow(1 + r / k, k), 1 / p) - 1;
  const effectiveInterestRateAfterInflationPerPeriod =
    Math.pow(Math.pow(1 + interestAfterInflation / k, k), 1 / p) - 1;
  const monthlyRateWithInflation =
    interestAfterInflation === 0
      ? G / d
      : G *
        (effectiveInterestRateAfterInflationPerPeriod /
          (1 -
            Math.pow(
              1 + effectiveInterestRateAfterInflationPerPeriod,
              -((d / 12) * p)
            )));

  const firstYearInterest = calculateFirstYearInterest({
    loanAmount: state.loanAmount,
    effectiveInterestRatePerPeriod,
    monthlyRate,
  });

  const avgMonthlyRateAfterSubsidy =
    monthlyRate -
    (Math.min(firstYearInterest, options.interestSubsidyLimit) *
      options.interestSubsidy) /
      12;

  const avgMonthlyFirstYearInterestRateSubsidy =
    monthlyRate - avgMonthlyRateAfterSubsidy;

  const handleLoanAmountChangeSlider = (newValue: number) => {
    dispatch({ type: "setAmount", amount: newValue });
  };

  const handleDurationChangeSlider = (newValue: number) => {
    dispatch({
      type: "setDuration",
      duration: newValue * 12,
    });
  };

  const handleInterestChangeSlider = (newValue: number) => {
    dispatch({
      type: "setInterest",
      interest: newValue,
    });
  };

  const handleInflationChangeSlider = (newValue: number) => {
    dispatch({
      type: "setInflation",
      inflation: newValue,
    });
  };

  const handleAccrualChangeSlider = (newValue: number) => {
    dispatch({
      type: "setAccruals",
      accruals: newValue,
    });
  };

  const handlePaymentChangeSlider = (newValue: number) => {
    dispatch({
      type: "setPayments",
      payments: newValue,
    });
  };

  return (
    <Calculator.Form autoComplete="off">
      <Calculator.MainContent>
        <div>
          <Calculator.Row>
            <Calculator.InputRow>
              <Calculator.Label>{translate("loanAmount")}</Calculator.Label>
              <Calculator.InputRowGroup>
                <NumberInput
                  value={state.loanAmount}
                  onChange={handleLoanAmountChangeSlider}
                  min={options.amountMin}
                  max={options.amountMax}
                  integer={true}
                  size={10}
                />
                <Calculator.UnitInputLabel>kr.</Calculator.UnitInputLabel>
              </Calculator.InputRowGroup>
            </Calculator.InputRow>
            <div style={{ width: "100%" }}>
              <Slider
                value={state.loanAmount}
                min={options.amountMin}
                max={options.amountMax}
                step={options.amountIncrement}
                unit={{ single: "kr.", plural: "kr." }}
                onChange={handleLoanAmountChangeSlider}
              />
            </div>
          </Calculator.Row>
          <Calculator.Row>
            <Calculator.InputRow>
              <Calculator.Label>{translate("duration")}</Calculator.Label>
              <Calculator.InputRowGroup>
                <NumberInput
                  value={state.durationInMonths / 12}
                  onChange={handleDurationChangeSlider}
                  min={options.durationMin / 12}
                  max={options.durationMax / 12}
                  integer={true}
                  size={2}
                />
                <Calculator.UnitInputLabel>
                  {state.durationInMonths / 12 === 1
                    ? translate("year")
                    : translate("years")}
                </Calculator.UnitInputLabel>
              </Calculator.InputRowGroup>
            </Calculator.InputRow>
            <div style={{ width: "100%" }}>
              <Slider
                value={state.durationInMonths / 12}
                min={options.durationMin / 12}
                max={options.durationMax / 12}
                step={options.durationIncrement / 12}
                unit={{
                  single: translate("year") as string,
                  plural: translate("years") as string,
                }}
                onChange={handleDurationChangeSlider}
              />
            </div>
          </Calculator.Row>
          <Calculator.Row>
            <Calculator.InputRow>
              <Calculator.Label>{translate("interest")}</Calculator.Label>
              <Calculator.InputRowGroup>
                <NumberInput
                  value={state.interestPercentage}
                  onChange={handleInterestChangeSlider}
                  min={options.interestMin}
                  max={options.interestMax}
                  size={4}
                />
                <Calculator.UnitInputLabel>%</Calculator.UnitInputLabel>
              </Calculator.InputRowGroup>
            </Calculator.InputRow>
            <div style={{ width: "100%" }}>
              <Slider
                value={round(state.interestPercentage)}
                min={options.interestMin}
                max={options.interestMax}
                step={options.interestIncrement}
                unit={{ single: "%", plural: "%" }}
                onChange={handleInterestChangeSlider}
              />
            </div>
          </Calculator.Row>
          {showAccruals && (
            <Calculator.Row>
              <Calculator.InputRow>
                <Calculator.Label>
                  {translate("accrualsPerYear")}
                </Calculator.Label>
                <Calculator.InputRowGroup>
                  <NumberInput
                    value={state.accrualsPerYear}
                    onChange={handleAccrualChangeSlider}
                    min={options.accrualsMin}
                    max={options.accrualsMax}
                    size={2}
                  />
                  <Calculator.UnitInputLabel>
                    {state.accrualsPerYear === 1
                      ? translate("time")
                      : translate("times")}
                  </Calculator.UnitInputLabel>
                </Calculator.InputRowGroup>
              </Calculator.InputRow>
              <div style={{ width: "100%" }}>
                <Slider
                  value={state.accrualsPerYear}
                  min={options.accrualsMin}
                  max={options.accrualsMax}
                  step={options.accrualsIncrement}
                  unit={{
                    single: translate("time") as string,
                    plural: translate("times") as string,
                  }}
                  onChange={handleAccrualChangeSlider}
                />
              </div>
            </Calculator.Row>
          )}
          {showPayments && (
            <Calculator.Row>
              <Calculator.InputRow>
                <Calculator.Label>
                  {translate("paymentsPerYear")}
                </Calculator.Label>
                <Calculator.InputRowGroup>
                  <NumberInput
                    value={state.paymentsPerYear}
                    onChange={handlePaymentChangeSlider}
                    min={options.paymentsMin}
                    max={options.paymentsMax}
                    size={3}
                  />
                  <Calculator.UnitInputLabel>
                    {state.accrualsPerYear === 1
                      ? translate("time")
                      : translate("times")}
                  </Calculator.UnitInputLabel>
                </Calculator.InputRowGroup>
              </Calculator.InputRow>
              <div style={{ width: "100%" }}>
                <Slider
                  value={state.paymentsPerYear}
                  min={options.paymentsMin}
                  max={options.paymentsMax}
                  step={options.paymentsIncrement}
                  unit={{
                    single: translate("time") as string,
                    plural: translate("times") as string,
                  }}
                  onChange={handlePaymentChangeSlider}
                />
              </div>
            </Calculator.Row>
          )}
        </div>
      </Calculator.MainContent>
      <Calculator.SecondaryContent>
        <Calculator.Row>
          <Calculator.Label $result>
            {translate("monthlyRate")}
          </Calculator.Label>
          <span
            style={{
              fontSize: theme.fontSize.huge,
              fontWeight: "bold",
              whiteSpace: "nowrap",
            }}
          >
            {(Math.round(monthlyRate * 100) / 100).toLocaleString("da-DK")} kr.
          </span>
        </Calculator.Row>
        {(showSubsidyAmount || showPaymentAfterSubsidy) && (
          <details
            open
            style={{
              border: `1px solid ${theme.color.darkGray}`,
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              padding: "5px 10px",
              borderRadius: theme.radius.medium,
              marginTop: theme.space.small,
            }}
          >
            <summary style={{ cursor: "pointer", userSelect: "none" }}>
              {translate("interestSubsidy")}
            </summary>
            {showSubsidyAmount && (
              <Calculator.Row>
                <Calculator.Label $result>
                  {translate("monthlyInterestSubsidy")}*
                </Calculator.Label>
                <span
                  style={{
                    fontSize: theme.fontSize.medium,
                    whiteSpace: "nowrap",
                  }}
                >
                  {(
                    Math.round(avgMonthlyFirstYearInterestRateSubsidy * 100) /
                    100
                  ).toLocaleString("da-DK")}{" "}
                  kr.
                </span>
              </Calculator.Row>
            )}
            {showPaymentAfterSubsidy && (
              <Calculator.Row>
                <Calculator.Label $result>
                  {translate("monthlyRateAfterInterestSubsidy")}*
                </Calculator.Label>
                <span
                  style={{
                    fontSize: theme.fontSize.medium,
                    whiteSpace: "nowrap",
                  }}
                >
                  {(
                    Math.round(avgMonthlyRateAfterSubsidy * 100) / 100
                  ).toLocaleString("da-DK")}{" "}
                  kr.
                </span>
                <span
                  style={{
                    fontSize: theme.fontSize.small,
                    whiteSpace: "nowrap",
                    marginTop: theme.space.medium,
                    color: theme.color.lightBlack,
                  }}
                >
                  *{translate("interestSubsidyDisclaimer")}
                </span>
              </Calculator.Row>
            )}
          </details>
        )}
        {showInflation && (
          <details
            style={{
              border: `1px solid ${theme.color.darkGray}`,
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              padding: "5px 10px",
              borderRadius: theme.radius.medium,
              marginTop: theme.space.small,
            }}
          >
            <summary style={{ cursor: "pointer", userSelect: "none" }}>
              {translate("inflation")}
            </summary>
            <>
              <Calculator.Row>
                <Calculator.InputRow>
                  <div></div>
                  <Calculator.InputRowGroup>
                    <NumberInput
                      value={state.inflationPercentage}
                      onChange={handleInflationChangeSlider}
                      min={options.inflationMin}
                      max={options.inflationMax}
                      size={4}
                    />
                    <Calculator.UnitInputLabel>%</Calculator.UnitInputLabel>
                  </Calculator.InputRowGroup>
                </Calculator.InputRow>
                <div style={{ width: "100%" }}>
                  <Slider
                    value={round(state.inflationPercentage)}
                    min={options.inflationMin}
                    max={options.inflationMax}
                    step={options.inflationIncrement}
                    unit={{ single: "%", plural: "%" }}
                    onChange={handleInflationChangeSlider}
                  />
                </div>
                <span
                  style={{
                    fontSize: theme.fontSize.small,
                    fontWeight: "normal",
                  }}
                >
                  {translate("interestAfterInflation")}:&nbsp;
                  <strong>
                    {round(interestAfterInflation * 100).toLocaleString(
                      "da-DK"
                    )}
                    %
                  </strong>
                </span>
              </Calculator.Row>
              <Calculator.Row>
                <Calculator.Label $result>
                  {translate("monthlyRateWithInflation")}
                </Calculator.Label>
                <span
                  style={{
                    fontSize: theme.fontSize.medium,
                    whiteSpace: "nowrap",
                  }}
                >
                  {(
                    Math.round(monthlyRateWithInflation * 100) / 100
                  ).toLocaleString("da-DK")}{" "}
                  kr.
                </span>
              </Calculator.Row>
            </>
          </details>
        )}
      </Calculator.SecondaryContent>
    </Calculator.Form>
  );
};

export default AnnuityCalculator;
